/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getAnnouncementFeeds, markAnnouncementsAsRead } from '../../services/Feed.service';
import {
  transformApiAnnouncementToDisplay,
  transformSocketEventToAnnouncement,
  checkUserRoleType,
  isAnnouncementUnread,
  stripNewTags,
} from '../../mappers/feedUtility';
import { FEED_TYPE, PAGINATION_CONSTANTS } from '../../constants';
import { ROLES } from '../../../../../constants';

/**
 * Hook for managing Announcements functionality
 * Handles announcement listing, type filtering, and pagination
 * Socket events are filtered based on the selected type
 */
export const useAnnouncementsHook = (isActive = false, decrementUnreadCount, incrementUnreadCount) => {
  // Get user details from Redux store
  const { userDetail } = useSelector((state) => state.app || {});
  const currentUserKey = userDetail?.userId;

  // State
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedType, setSelectedType] = useState('My Announcement');
  const selectedTypeRef = useRef(selectedType); // Ref to track current selectedType
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);

  /**
   * Map UI type values to API type values
   * UI uses singular form, API uses uppercase with underscore
   */
  const mapTypeToApiValue = useCallback((uiType) => {
    switch (uiType) {
      case 'All Announcement':
        return 'ALL_ANNOUNCEMENTS';
      case 'My Announcement':
        return 'MY_ANNOUNCEMENTS';
      default:
        return 'ALL_ANNOUNCEMENTS';
    }
  }, []);

  /**
   * Transform announcement data with event handlers
   */
  const transformAnnouncementWithHandlers = useCallback((announcement) => {
    return transformApiAnnouncementToDisplay(announcement);
  }, []);

  /**
   * Update announcements state based on reset flag
   */
  const updateAnnouncementsState = useCallback((newAnnouncements, shouldReset) => {
    setAnnouncements((prev) => shouldReset ? newAnnouncements : [...prev, ...newAnnouncements]);
  }, []);

  /**
   * Reset announcements state to empty
   */
  const resetAnnouncementsState = useCallback(() => {
    setAnnouncements([]);
    setTotalCount(0);
    setHasMore(false);
  }, []);

  /**
   * Process announcements for ALL_ANNOUNCEMENTS type
   * Removes 'New' tags and marks as read for display
   */
  const processAllAnnouncements = useCallback((announcements) => {
    return announcements.map((announcement) => ({
      ...announcement,
      isNew: false,
      tags: stripNewTags(announcement.tags || []),
    }));
  }, []);

  /**
   * Filter announcements that need to be marked as read
   * Only includes announcements with readAt explicitly set to null
   */
  const filterUnreadAnnouncements = useCallback((announcements) => {
    return announcements
      .filter((announcement) => isAnnouncementUnread(announcement))
      .map((announcement) => announcement.id)
      .filter((id) => id);
  }, []);

  /**
   * Handle decrement of unread count
   * Safely calls the callback if provided
   */
  const handleUnreadCountDecrement = useCallback((count) => {
    if (typeof decrementUnreadCount === 'function') {
      try {
        decrementUnreadCount(FEED_TYPE.ANNOUNCEMENT, count);
      } catch (error) {
        console.error('[Announcements] Error decrementing unread count:', error);
      }
    }
  }, [decrementUnreadCount]);

  /**
   * Handle increment of unread count
   * Safely calls the callback if provided
   */
  const handleUnreadCountIncrement = useCallback(() => {
    if (typeof incrementUnreadCount === 'function') {
      try {
        incrementUnreadCount(FEED_TYPE.ANNOUNCEMENT);
      } catch (error) {
        console.error('[Announcements] Error incrementing unread count:', error);
      }
    }
  }, [incrementUnreadCount]);

  /**
   * Update announcement state after marking as read
   */
  const updateAnnouncementsAfterMarkAsRead = useCallback((markedIds) => {
    handleUnreadCountDecrement(markedIds.length);
    setAnnouncements((prevAnnouncements) => {
      const idsSet = new Set(markedIds);
      let didChange = false;

      const updatedAnnouncements = prevAnnouncements.map((announcement) => {
        if (!announcement || !idsSet.has(announcement.id)) {
          return announcement;
        }

        const cleanedTags = stripNewTags(announcement.tags || []);
        const shouldUpdate = announcement.isNew || cleanedTags.length !== (announcement.tags || []).length;

        if (!shouldUpdate) {
          return announcement;
        }

        didChange = true;
        return {
          ...announcement,
          isNew: false,
          tags: cleanedTags,
          readAt: announcement.readAt || new Date().toISOString(),
        };
      });

      return didChange ? updatedAnnouncements : prevAnnouncements;
    });
  }, [handleUnreadCountDecrement]);

  /**
   * Mark announcements as read via API
   */
  const markAnnouncementsAsReadAsync = useCallback(async (content) => {
    const announcementIdsToMark = filterUnreadAnnouncements(content);

    if (announcementIdsToMark.length === 0) {
      return;
    }

    try {
      await markAnnouncementsAsRead(announcementIdsToMark);
      updateAnnouncementsAfterMarkAsRead(announcementIdsToMark);
    } catch (error) {
      console.error('[Announcements] Failed to mark announcements as read:', error);
    }
  }, [filterUnreadAnnouncements, updateAnnouncementsAfterMarkAsRead]);

  /**
   * Load announcements from API
   */
  const loadAnnouncements = useCallback(
    async (type, page, shouldReset, size = PAGINATION_CONSTANTS.DEFAULT_ANNOUNCEMENT_PAGE_SIZE) => {
      setIsLoading(true);
      try {
        const apiType = mapTypeToApiValue(type);
        const response = await getAnnouncementFeeds({
          type: apiType,
          page: page,
          size: size,
        });

        const { content = [], totalElements = 0, hasMore: responseHasMore = false } = response.data || {};

        // Transform announcements
        let transformedAnnouncements = content.map(transformAnnouncementWithHandlers);

        // Process based on announcement type
        if (apiType === 'ALL_ANNOUNCEMENTS') {
          transformedAnnouncements = processAllAnnouncements(transformedAnnouncements);
        }

        // Update state
        updateAnnouncementsState(transformedAnnouncements, shouldReset);
        setTotalCount(totalElements);
        setHasLoadedInitial(true);
        setHasMore(responseHasMore);

        // Mark MY_ANNOUNCEMENTS as read
        if (apiType === 'MY_ANNOUNCEMENTS') {
          await markAnnouncementsAsReadAsync(content);
        }
      } catch (error) {
        console.error('[Announcements] Failed to load announcements:', error);
        if (shouldReset) {
          resetAnnouncementsState();
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      mapTypeToApiValue,
      transformAnnouncementWithHandlers,
      processAllAnnouncements,
      updateAnnouncementsState,
      resetAnnouncementsState,
      markAnnouncementsAsReadAsync,
    ]
  );


  /**
   * Handle type change (All Announcements / My Announcements)
   */
  const handleTypeChange = useCallback(
    (newType) => {
      setIsLoading(true);
      setAnnouncements([]);
      setSelectedType(newType);
      setCurrentPage(1);
      loadAnnouncements(newType, 1, true, 10);
    },
    [loadAnnouncements]
  );

  /**
   * Handle load more pagination
   */
  const handleLoadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      loadAnnouncements(selectedType, nextPage, false, 10);
    }
  }, [hasMore, isLoading, currentPage, selectedType, loadAnnouncements]);

  /**
   * Load initial announcements when tab becomes active
   */
  useEffect(() => {
    if (isActive && !hasLoadedInitial) {
      loadAnnouncements(selectedType, 1, true, 10);
    }
  }, [isActive, hasLoadedInitial, loadAnnouncements, selectedType]);

  /**
   * Reload announcements when returning to tab (after initial load)
   * This ensures fresh data after creating announcements
   */
  useEffect(() => {
    if (isActive && hasLoadedInitial) {
      setCurrentPage(1); // Reset page to 1 when returning to tab
      loadAnnouncements(selectedType, 1, true, 10);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]); // Only depends on isActive to trigger on tab switches

  /**
   * Expose refetch function to reload announcements
   * Can be called after creating an announcement
   * Uses ref to always get the latest selectedType value
   */
  const refetchAnnouncements = useCallback(() => {
    const currentType = selectedTypeRef.current;
    loadAnnouncements(currentType, 1, true, 10);
  }, [loadAnnouncements]);


    /**
   * Keep ref in sync with selectedType state
   */
    useEffect(() => {
      selectedTypeRef.current = selectedType;
    }, [selectedType]);

  /**
   * Mark socket announcement as read after adding to list
   * Shows the "New" tag momentarily, then marks as read via API call
   */
  const markSocketAnnouncementAsRead = useCallback(async (announcementId) => {
    try {
      await markAnnouncementsAsRead([announcementId]);
      
      handleUnreadCountDecrement(1);
      // Update the announcement in state to remove "New" tag and set readAt
      setAnnouncements((prevAnnouncements) => {
        return prevAnnouncements.map((announcement) => {
          if (announcement.id === announcementId) {
            return {
              ...announcement,
              isNew: false,
              tags: stripNewTags(announcement.tags || []),
              readAt: new Date().toISOString(),
            };
          }
          return announcement;
        });
      });
    } catch (error) {
      console.error('[Announcements] Failed to mark socket announcement as read:', error);
    }
  }, []);
  /**
   * Handle new announcement feed creation from socket event
   * Filters announcements based on selected type (All/My Announcements)
   * "My Announcement" includes announcements where user is sender OR member
   * Shows "New" tag momentarily and marks as read immediately via API
   */
  const onAnnouncementCreated = useCallback((eventData) => {
    console.log('[Announcements] New announcement created:', eventData);

    // 🚨 Exit if user not identified
    if (!currentUserKey) {
      console.log('[Announcements] Skipping - no current user key');
      return;
    }

    // 🧑‍💻 Identify sender and members
    const senderId = String(eventData.sender_id);
    const members = eventData.members_info || [];
    const isSender = senderId === String(currentUserKey);
    const isMember = members.some(m => String(m.id) === String(currentUserKey));
    const isMyAnnouncement = isSender || isMember;

    // 🔔 Increment unread count for recipients only
    if (isMember && !isSender) handleUnreadCountIncrement();

    // 🧭 Filter based on selected tab
    const isViewingMyAnnouncements = selectedType === 'My Announcement';
    const isViewingAllAnnouncements = selectedType === 'All Announcement';

    if (isViewingMyAnnouncements && !isMyAnnouncement) {
      console.log('[Announcements] Skipping - not relevant to current user');
      return;
    }

    if (isViewingAllAnnouncements && isMyAnnouncement) {
      console.log('[Announcements] Skipping - belongs to "My Announcement"');
      return;
    }

    // 🧱 Transform event data
    let announcement = transformSocketEventToAnnouncement(eventData);

    // 🧽 Remove "New" tag if:
    //    - Viewing All Announcements (always), OR
    //    - Viewing My Announcements AND user is the sender
    if (isViewingAllAnnouncements || (isViewingMyAnnouncements && isSender)) {
      announcement = {
        ...announcement,
        isNew: false,
        tags: stripNewTags(announcement.tags || []),
      };
    }

    // 📥 Add to state (prepend new announcement)
    setAnnouncements(prev => [announcement, ...prev]);
    setTotalCount(prev => prev + 1);

    // ✅ Mark as read if viewing My Announcements (keeps "New" tag momentarily for members only)
    if (isViewingMyAnnouncements && !isSender && announcement.id) {
      markSocketAnnouncementAsRead(announcement.id);
    }

  }, [selectedType, currentUserKey, markSocketAnnouncementAsRead, handleUnreadCountIncrement]);

  /**
   * Handle new message in announcement feed from socket event
   */
  const onAnnouncementMessageReceived = useCallback((eventData) => {
    console.log('[Announcements] New announcement message received:', eventData);
    
    // For announcement messages, we could either:
    // 1. Refetch the entire announcement to get updated message count
    // 2. Update the existing announcement in the list with new message info
    
    // For now, let's refetch to ensure data consistency
    refetchAnnouncements();
  }, [refetchAnnouncements]);

  /**
   * Check if user has permission to create announcements
   */
  const showNewAnnouncementButton = useMemo(() => {
    return checkUserRoleType([ROLES.SUPER_ADMIN, ROLES.STAFF, ROLES.MESSAGING_ONLY, ROLES.TEACHER]);
  }, []);

  const onAnnouncementDeleteHandler = useCallback((announcementId) => {
    setAnnouncements((prev) => prev.filter((announcement) => announcement.id !== announcementId));
    setTotalCount((prev) => prev - 1);
  }, []);
  /**
   * Return object matching the frontend commons API structure
   */
  return {
    announcements, // Direct list (no filtering needed since no search)
    selectedType,
    isLoading,
    showLoadMore: hasMore && !isLoading,
    showNewAnnouncementButton,
    allAnnouncementText: 'All Announcement',
    myAnnouncementText: 'My Announcement',
    totalAnnouncementCount: totalCount,
    onAnnouncementTypeChangeHandler: handleTypeChange,
    onLoadMore: handleLoadMore,
    refetchAnnouncements,
    // Socket event handlers
    onAnnouncementCreated,
    onAnnouncementMessageReceived,
    onAnnouncementDeleteHandler
  };
};
